/**
 * Constantes du widget météo : config des 4 variables (T° / pluie / vent / hum.),
 * dimensions SVG, libellés temporels FR, TTL React Query.
 *
 * Aucun JSX ici — uniquement des données statiques.
 */

import { CloudRain, Droplets, Thermometer, Wind } from "lucide-react";

import type { VariableConfig, VariableMeteo } from "./types.js";

/**
 * Mapping des 4 variables affichables — couleur, unité, extracteur, bornes.
 * Les commentaires sur `humidity.bornes = null` documentent le choix d'auto-scale.
 */
export const VARIABLES: Record<VariableMeteo, VariableConfig> = {
  temperature: {
    cle: "temperature",
    libelleOnglet: "Temp.",
    libelleLegende: "Température horaire (°C)",
    iconeOnglet: Thermometer,
    couleur: "#C77A3B",
    opaciteHaut: 0.22,
    unite: "°",
    extracteur: (p) => p.temperature_c,
    bornes: null, // auto-scale (min/max sur la fenêtre 7 jours)
  },
  precipitation: {
    cle: "precipitation",
    libelleOnglet: "Pluie",
    libelleLegende: "Précipitations horaires (mm)",
    iconeOnglet: CloudRain,
    couleur: "#5B7A95",
    opaciteHaut: 0.3,
    unite: " mm",
    extracteur: (p) => p.precipitation_mm,
    bornes: { min: 0, max: 8 }, // 8 mm/h = pluie soutenue, au-delà la barre sature visuellement
  },
  wind: {
    cle: "wind",
    libelleOnglet: "Vent",
    libelleLegende: "Vent moyen à 10 m (km/h) — au-dessus de 20 km/h, pas de pulvérisation phyto",
    iconeOnglet: Wind,
    couleur: "#7B8B6E",
    opaciteHaut: 0.22,
    unite: " km/h",
    extracteur: (p) => p.wind_kmh,
    bornes: { min: 0, max: 40 },
  },
  humidity: {
    cle: "humidity",
    libelleOnglet: "Hum.",
    libelleLegende: "Humidité relative à 2 m (%) — au-dessus de 90 % de nuit, risque mildiou",
    iconeOnglet: Droplets,
    couleur: "#6B8E8E",
    opaciteHaut: 0.2,
    unite: "%",
    extracteur: (p) => p.humidity_pct,
    // Auto-scale : en Champagne l'humidité descend rarement sous 40-50 %,
    // un graphe figé 0-100 % écraserait visuellement la dynamique réelle.
    bornes: null,
  },
};

// Dimensions SVG en unités logiques. Hauteur fixe, largeur = container mesuré.
export const SVG_HEIGHT = 170;
export const SVG_PAD_LEFT = 32;
export const SVG_PAD_RIGHT = 8;
export const SVG_PAD_TOP = 8;
export const SVG_PAD_BOTTOM = 36;

// Largeur fallback du container avant la 1ère mesure du ResizeObserver
// (≈ largeur réelle d'un widget dashboard 2-col sur écran 1280px). Évite
// un flash de layout 0px au 1er render.
export const DEFAULT_CONTAINER_WIDTH_PX = 688;
// Largeur min sous laquelle on ne resserre pas (sinon le SVG devient illisible).
export const MIN_CONTAINER_WIDTH_PX = 280;

// staleTime React Query — aligné sur le TTL backend (`METEO_TTL_MS` côté API).
// Pas de refetch automatique pendant 30 min pour économiser la 4G terrain.
export const METEO_STALE_TIME_MS = 30 * 60 * 1000;

// Périodicité du tick "maintenant" (recalcule l'index horaire courant sans
// déclencher de refetch tRPC). 5 min suffisent : le marker bouge d'une case
// à chaque tick.
export const TICK_MAINTENANT_MS = 5 * 60 * 1000;

/** Libellés courts pour le strip 7 jours + libellés axes graphique. */
export const JOURS_COURT = ["DIM", "LUN", "MAR", "MER", "JEU", "VEN", "SAM"];

/** Libellés longs pour le panneau "heure explorée". */
export const JOURS_LONG = ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."];

export const MOIS_LONG = [
  "janv.",
  "févr.",
  "mars",
  "avr.",
  "mai",
  "juin",
  "juil.",
  "août",
  "sept.",
  "oct.",
  "nov.",
  "déc.",
];
